import { useCallback } from 'react';

import { isHydratedReward, mapLegacyToMinimalCartEntries } from 'features/cart/utils/minimal-cart';
import { useStoreContext } from 'features/store';
import { isEqual } from 'lodash-es';

import { ICombo, ILegacyCartEntry } from '@rbi-ctg/menu';
import { toaster } from 'components/toaster/toaster';
import { useHydrateCartMutation } from 'generated/graphql-gateway';
import { CustomEventNames, EventTypes, logAddOrRemoveFromCart, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { removeAppliedOffersInStorage } from 'state/global-state/models/loyalty/offers/offers.utils';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { t } from 'state/intl/translations';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser, useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useMenuContext } from 'state/menu';
import { getServiceModeFromLS } from 'state/service-mode';
import { sanitizeCartForReprice } from 'utils/cart';
import { computeCartTotal } from 'utils/cart/helper';
import { routes } from 'utils/routing';

import { hydrateEntries } from './hydrateEntries';
import { useRoute } from './navigation/use-route';
import { IUseCart } from './use-cart-types';
import { useEnableUnifiedMenu } from './use-enable-unified-menu';
import { useRepriceCartEntries } from './use-reprice-cart-entries';

/**
 * Expose the cart state and the utility methods to mutate it.
 *
 * When an action to mutate the cart is triggered, it manages the Redux cart actions, logs the events,
 * coordinates with other parts of the code, and dispatches actions on related slices.
 */
export const useCart = (): IUseCart => {
  const dispatch = useAppDispatch();
  const cartEntries = useAppSelector(selectors.ordering.selectCartEntries);
  const { loyaltyUser } = useLoyaltyUser();
  const { loyaltyUserId } = useLoyaltyUserId();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const discountAppliedCmsOffers = useAppSelector(selectors.loyalty.selectDiscountAppliedCmsOffer);

  const { pathname } = useRoute();

  const shouldDisplayMenuToast = !!pathname.startsWith(routes.menu);

  const { pricingFunction } = useMenuContext();

  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const { store } = useStoreContext();
  const storeId = store?.number;
  const { isAuthenticated } = useAuthContext();
  const [hydrateCart] = useHydrateCartMutation();

  const updateCartEntry = useCallback(
    (
      newCartEntry: ILegacyCartEntry,
      cartId: string,
      skipShowMessage?: boolean,
      fromHydration?: boolean
    ) => {
      const originalEntry = cartEntries.find(entry => entry.cartId === cartId);
      if (!originalEntry) {
        return;
      }

      dispatch(
        actions.ordering.updateCartEntry({
          cartEntry: { ...newCartEntry, ...(fromHydration ? { image: originalEntry.image } : {}) },
          cartId,
        })
      );

      // TODO: analyze moving this to extraReducers on loyalty slice
      // If the original entry has a reward applied but the new entry is a different item,
      // then remove the reward if it is not a picker selection.
      // This logic will apply only for legacy since we don't have pickerSelections for one menu pickers.
      const isPickerSelection = Object.keys(originalEntry.pickerSelections).length > 0;
      const appliedReward = appliedLoyaltyRewards[originalEntry?.cartId];
      if (
        !isUnifiedMenuEnabled &&
        !isPickerSelection &&
        appliedReward &&
        appliedReward.rewardBenefitId !== newCartEntry._id &&
        !isHydratedReward(newCartEntry)
      ) {
        dispatch(
          actions.loyalty.removeAppliedReward({
            rewardBenefitId: appliedReward.rewardBenefitId,
            cartId: originalEntry.cartId,
          })
        );
      }

      logAddOrRemoveFromCart({
        action: 'remove',
        cartEntry: originalEntry,
        previousCartEntries: [originalEntry],
        isReward: !!appliedReward,
      });
      logAddOrRemoveFromCart({
        action: 'add',
        cartEntry: newCartEntry,
        previousCartEntries: [originalEntry],
      });

      if (shouldDisplayMenuToast && !skipShowMessage) {
        return setTimeout(() => {
          return toaster.addToCartToast(
            `${t('updateCartSuccess', { itemName: newCartEntry.name })}`,
            newCartEntry.image
          );
        }, 100);
      }

      if (!skipShowMessage) {
        return toaster.positive(t('updateCartSuccess', { itemName: newCartEntry.name }));
      }

      return;
    },
    [appliedLoyaltyRewards, cartEntries, dispatch, isUnifiedMenuEnabled, shouldDisplayMenuToast]
  );

  /**
   * Adds a single cartEntry to the cart.
   * If the cartEntry is a donation, it will not show a toast message.
   * If the unified menu is enabled, it will hydrate the cart entries.
   * It will log the event of adding the cart entry.
   */
  const addItemToCart = useCallback<IUseCart['addItemToCart']>(
    (cartEntry, selectionAttrs, showMessage = true, fromHydration = false) => {
      const shouldDisplayToast = showMessage && !cartEntry.isDonation;

      if (isUnifiedMenuEnabled && !fromHydration) {
        const serviceMode = getServiceModeFromLS();

        if (!storeId || !serviceMode || !isAuthenticated) {
          return;
        }

        const minimalCartEntries = mapLegacyToMinimalCartEntries([cartEntry]);

        hydrateEntries(
          minimalCartEntries,
          storeId,
          serviceMode,
          hydrateCart,
          addItemToCart,
          updateCartEntry,
          dispatch,
          undefined,
          0,
          loyaltyUserId
        );

        if (shouldDisplayToast) {
          toaster.addToCartToast(
            `${t('addToCartSuccess', { itemName: cartEntry.name })}`,
            cartEntry.image
          );
        }

        return;
      }

      logAddOrRemoveFromCart({
        action: 'add',
        cartEntry,
        previousCartEntries: cartEntries,
        selectionAttrs,
      });

      dispatch(actions.ordering.addCartEntry(cartEntry));

      if (shouldDisplayToast) {
        // Menu Toast
        if (shouldDisplayMenuToast) {
          return toaster.addToCartToast(
            `${t('addToCartSuccess', { itemName: cartEntry.name })}`,
            cartEntry.image
          );
        }

        // Regular Toast
        return toaster.positive(t('addToCartSuccess', { itemName: cartEntry.name }));
      }
    },
    [
      cartEntries,
      dispatch,
      hydrateCart,
      isAuthenticated,
      isUnifiedMenuEnabled,
      loyaltyUserId,
      shouldDisplayMenuToast,
      storeId,
      updateCartEntry,
    ]
  );

  const logCartEntryRemovedFromCart = useCallback(
    (cartEntry: ILegacyCartEntry) => {
      const appliedReward = appliedLoyaltyRewards[cartEntry?.cartId];
      logAddOrRemoveFromCart({
        action: 'remove',
        cartEntry,
        previousCartEntries: cartEntries,
        isReward: !!appliedReward,
      });
      if (cartEntry.isUpsell) {
        logEvent(CustomEventNames.UPSELL_REMOVED, EventTypes.Other, {
          Id: cartEntry._id,
          Name: cartEntry.name,
          Price: cartEntry.price && cartEntry.price / 100,
        });
      }
    },
    [appliedLoyaltyRewards, cartEntries]
  );

  const removeRewardIfNeeded = useCallback(
    (cartEntry: ILegacyCartEntry) => {
      const { cartId } = cartEntry;

      // Unavailable rewards should be included because they could be applied and
      // we want to remove them from applied rewards anyways
      const cartEntryReward = getAvailableRewardFromCartEntry(cartEntry, {
        includeUnavailable: true,
        isOneMenu: isUnifiedMenuEnabled,
      });

      const isRewardApplied = !!appliedLoyaltyRewards?.[cartId]?.timesApplied;
      if (loyaltyEnabled && isRewardApplied && cartEntryReward) {
        dispatch(
          actions.loyalty.removeAppliedReward({
            rewardBenefitId: cartEntryReward.rewardBenefitId,
            cartId,
          })
        );
      }
    },
    [
      getAvailableRewardFromCartEntry,
      isUnifiedMenuEnabled,
      appliedLoyaltyRewards,
      loyaltyEnabled,
      dispatch,
    ]
  );

  // Removes a single cartEntry using its cartId
  const removeFromCart = useCallback<IUseCart['removeFromCart']>(
    ({ cartId }: { cartId: string }) => {
      dispatch(actions.ordering.removeCartEntries(new Set([cartId])));
      dispatch(actions.loyalty.removeAppliedOfferByCartEntry({ cartId } as ILegacyCartEntry));
      const cartEntryToRemove = cartEntries.find(entry => entry.cartId === cartId);

      if (!cartEntryToRemove) {
        return;
      }

      // removes applied rewards associated to cart entry on removal
      removeRewardIfNeeded(cartEntryToRemove);
      logCartEntryRemovedFromCart(cartEntryToRemove);
    },
    [cartEntries, dispatch, logCartEntryRemovedFromCart, removeRewardIfNeeded]
  );

  // Removes all provided cartEntries from the cart using their cartIds
  const removeAllFromCart = useCallback<IUseCart['removeAllFromCart']>(
    (cartEntriesToRemove = []) => {
      const cartEntryIdsToRemove = new Set(
        cartEntriesToRemove.map((entry: ILegacyCartEntry) => entry.cartId)
      );

      dispatch(actions.ordering.removeCartEntries(cartEntryIdsToRemove));

      // Collect cartIds that have rewards applied for batch removal
      const cartIdsWithRewards: string[] = [];
      if (loyaltyEnabled) {
        cartEntriesToRemove.forEach(cartEntry => {
          const { cartId } = cartEntry;
          const isRewardApplied = !!appliedLoyaltyRewards?.[cartId]?.timesApplied;
          const cartEntryReward = getAvailableRewardFromCartEntry(cartEntry, {
            includeUnavailable: true,
            isOneMenu: isUnifiedMenuEnabled,
          });
          if (isRewardApplied && cartEntryReward) {
            cartIdsWithRewards.push(cartId);
          }
        });
      }

      // Batch remove all applied rewards in a single dispatch
      if (cartIdsWithRewards.length > 0) {
        dispatch(actions.loyalty.removeAppliedRewards({ cartIds: cartIdsWithRewards }));
      }

      // Handle offer removal and logging for each entry
      cartEntriesToRemove.forEach(cartEntry => {
        dispatch(actions.loyalty.removeAppliedOfferByCartEntry(cartEntry));
        logCartEntryRemovedFromCart(cartEntry);
      });
    },
    [
      dispatch,
      logCartEntryRemovedFromCart,
      loyaltyEnabled,
      appliedLoyaltyRewards,
      getAvailableRewardFromCartEntry,
    ]
  );

  const updateQuantity = useCallback<IUseCart['updateQuantity']>(
    (cartId: string, quantity: number) => {
      const cartEntryToUpdate = cartEntries.find(cartEntry => cartEntry.cartId === cartId);
      if (!cartEntryToUpdate) {
        return;
      }

      if (quantity < 1) {
        return removeFromCart({ cartId });
      }
      const price = pricingFunction({ item: cartEntryToUpdate, quantity });
      dispatch(actions.ordering.updateQuantityAndPrice({ cartId, quantity, price }));
    },
    [cartEntries, dispatch, pricingFunction, removeFromCart]
  );

  const repriceCartEntriesHelper = useRepriceCartEntries();
  const repriceCartEntries = useCallback(
    (entries: ILegacyCartEntry[], parentEntry?: ILegacyCartEntry, mainCombo?: ICombo) => {
      // Ensure we have the proper cart entries defaults before repricing
      // This needs to be done because 1M and Legacy Cart entries have different defaults.
      const sanitizedCartEntries = sanitizeCartForReprice(entries);
      const newEntries = repriceCartEntriesHelper(sanitizedCartEntries, parentEntry, mainCombo);
      return isEqual(entries, newEntries) ? entries : newEntries;
    },
    [repriceCartEntriesHelper]
  );

  const emptyCart = useCallback(() => {
    dispatch(actions.ordering.resetCart());
    dispatch(actions.ordering.setPricedOrderId(null));

    removeAppliedOffersInStorage();
    removeAppliedRewardsInStorage();

    // TODO: evaluate moving this to `extraReducers` on loyalty slices
    if (loyaltyEnabled) {
      dispatch(actions.loyalty.resetAppliedOffers());
      dispatch(
        actions.loyalty.resetLoyaltyRewardsState({
          points: loyaltyUser?.points ?? 0,
          shouldResetAvailableRewardsMap: false,
        })
      );
    }
  }, [loyaltyEnabled, loyaltyUser, dispatch]);

  const isCartEmpty = !cartEntries?.length;

  const calculateCartTotalWithDiscount = useCallback(
    (paramCartEntries?: ILegacyCartEntry[]) => {
      const cartEntriesToUse = (paramCartEntries || cartEntries).map(entry => ({
        ...entry,
        price: entry.cartId === DISCOUNT_OFFER_CART_ID ? 0 : entry.price,
      }));
      const total = computeCartTotal(cartEntriesToUse, {
        loyaltyEnabled,
        appliedLoyaltyRewards,
        appliedLoyaltyOfferDiscount: discountAppliedCmsOffers?.incentives?.[0] as any,
      });
      return {
        cartTotal: total,
        isCartTotalNegative: total < 0,
      };
    },
    [appliedLoyaltyRewards, cartEntries, loyaltyEnabled, discountAppliedCmsOffers]
  );

  const calculateCartTotal = useCallback(() => {
    const { cartTotal, isCartTotalNegative } = calculateCartTotalWithDiscount(cartEntries);
    return isCartTotalNegative ? 0 : cartTotal;
  }, [calculateCartTotalWithDiscount, cartEntries]);

  return {
    addItemToCart,
    cartEntries,
    calculateCartTotal,
    calculateCartTotalWithDiscount,
    emptyCart,
    isCartEmpty,
    logCartEntryRemovedFromCart,
    removeAllFromCart,
    repriceCartEntries,
    removeFromCart,
    updateCartEntry,
    updateQuantity,
  };
};
